import { useMemo } from "react";

import { Grid, Stack, Typography } from "@mui/material";
import { ClaimParticipantSearch, FormikTextField } from "@shared";

import { ProceedingParticipantType, SrvProceedingResponse } from "@/api";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDatePicker } from "@/common/components/fields/formik/formik-date-picker/FormikDatePicker";
import { useCourts } from "@/common/hooks/useCourts";
import { SrvProceedingFormik } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/srv-proceeding-tab/SrvProceedingTab.types";
import { useTranslation } from "@/translations";

interface Props {
  formik: SrvProceedingFormik;
  proceeding?: SrvProceedingResponse;
  isInactiveTrusteeSelected: boolean;
  isInactiveInsolvencyAdministratorSelected: boolean;
}

export function ProceedingDataSection({
  formik,
  proceeding,
  isInactiveTrusteeSelected,
  isInactiveInsolvencyAdministratorSelected,
}: Props) {
  const { t } = useTranslation();
  const { courts } = useCourts();

  const courtOptions = useMemo(
    () => courts.map((court) => ({ id: court.id, label: court.name })).sort((a, b) => a.label.localeCompare(b.label)),
    [courts]
  );

  return (
    <Stack gap={3}>
      <Typography variant="h5">{t("clients.client.debtSettlement.srvProceeding.sections.proceedingData")}</Typography>
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid size={6}>
          <FormikDatePicker
            formik={formik}
            path="openingDate"
            label={t("clients.client.debtSettlement.srvProceeding.fields.openingDate")}
          />
        </Grid>
        <Grid size={6}>
          <FormikDatePicker
            formik={formik}
            path="annulmentDate"
            label={t("clients.client.debtSettlement.srvProceeding.fields.annulmentDate")}
          />
        </Grid>
        <Grid size={6}>
          <FormikAutocompleteSelect
            formik={formik}
            path="courtId"
            label={t("clients.client.debtSettlement.srvProceeding.fields.court")}
            options={courtOptions}
          />
        </Grid>
        <Grid size={6}>
          <FormikTextField
            formik={formik}
            path="fileNumber"
            label={t("clients.client.debtSettlement.srvProceeding.fields.fileNumber")}
            placeholder={t("common.fileNumber.placeholder")}
          />
        </Grid>
        <Grid size={6}>
          <ClaimParticipantSearch
            formik={formik}
            participantIdField="trusteeId"
            types={[ProceedingParticipantType.Trustee]}
            initialDisplayName={proceeding?.trusteeName ?? undefined}
            initialParticipantId={proceeding?.trusteeId ?? undefined}
            label={t("clients.client.debtSettlement.srvProceeding.fields.trustee")}
            entityLabel={t("clients.client.debtSettlement.srvProceeding.fields.trustee")}
            inactiveError={
              isInactiveTrusteeSelected
                ? t("clients.client.debtSettlement.srvProceeding.fields.inactiveTrustee")
                : undefined
            }
          />
        </Grid>
        <Grid size={6}>
          <ClaimParticipantSearch
            formik={formik}
            participantIdField="insolvencyAdministratorId"
            types={[ProceedingParticipantType.InsolvencyAdministrator]}
            initialDisplayName={proceeding?.insolvencyAdministratorName ?? undefined}
            initialParticipantId={proceeding?.insolvencyAdministratorId ?? undefined}
            label={t("clients.client.debtSettlement.srvProceeding.fields.insolvencyAdministrator")}
            entityLabel={t("clients.client.debtSettlement.srvProceeding.fields.insolvencyAdministrator")}
            inactiveError={
              isInactiveInsolvencyAdministratorSelected
                ? t("clients.client.debtSettlement.srvProceeding.fields.inactiveInsolvencyAdministrator")
                : undefined
            }
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
